// Dados de exemplo determinísticos para o mock da console.
// Nomes de empresas, pessoas e máquinas são fictícios.
import type * as T from "../types";

const DAY = 86_400_000;
const GB = 1e9;
const TB = 1e12;

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

function hex(rng: () => number, len: number): string {
  let s = "";
  while (s.length < len) s += Math.floor(rng() * 16).toString(16);
  return s;
}

const iso = (ms: number) => new Date(ms).toISOString();
const isoDate = (ms: number) => iso(ms).slice(0, 10);

/** Meia-noite local de hoje, para alinhar a régua de dias. */
function startOfToday(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// ---------------------------------------------------------------------------
// Organizações

interface OrgProfile {
  id: string;
  name: string;
  kind: "msp" | "customer";
  parentId: string | null;
  prefix: string;
  machines: number;
  fiscalArchive?: boolean;
}

const ORGS: OrgProfile[] = [
  { id: "msp-nuvemserra", name: "Nuvem Serra TI", kind: "msp", parentId: null, prefix: "NS", machines: 6 },
  { id: "org-serraazul", name: "Transportes Serra Azul", kind: "customer", parentId: "msp-nuvemserra", prefix: "SA", machines: 32, fiscalArchive: true },
  { id: "org-vidaplena", name: "Clínica Vida Plena", kind: "customer", parentId: "msp-nuvemserra", prefix: "VP", machines: 18 },
  { id: "org-horizonte", name: "Colégio Horizonte", kind: "customer", parentId: "msp-nuvemserra", prefix: "CH", machines: 24 },
  { id: "org-mouralima", name: "Moura & Lima Advocacia", kind: "customer", parentId: "msp-nuvemserra", prefix: "ML", machines: 9 },
];

export const HOME_ORG = "org-serraazul";

/** addOrg registra uma organização criada na simulação, sem máquina nenhuma. */
export function addOrg(name: string, kind: "msp" | "customer", parentId: string | null, quotaBytes: number | null): T.Organization {
  const id = `org-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "nova"}-${ORGS.length}`;
  ORGS.push({ id, name, kind, parentId, prefix: name.slice(0, 2).toUpperCase(), machines: 0 });
  newOrgQuota.set(id, quotaBytes);
  return { id, name, kind, parentId, machineCount: 0, protectedBytes: 0, storedBytes: 0, quotaBytes, status: "ok", closedAt: null, purgeAfter: null, purgeError: "", ...trialOf(id) };
}

// Teste gratuito na simulação (ADR 0038): a advocacia está em teste, faltando
// alguns dias, para a faixa da console e o selo da lista aparecerem.
const trialEnds = new Map<string, string | null>([["org-mouralima", new Date(Date.now() + 12 * 86_400_000).toISOString()]]);

function trialOf(orgId: string): Pick<T.Organization, "trialEndsAt" | "machineLimit" | "trialRestoreUntil"> {
  const ends = trialEnds.get(orgId) ?? null;
  // Prazo para restaurar: 15 dias depois do fim, o padrão do pool de teste.
  const restore = ends ? new Date(new Date(ends).getTime() + 15 * 86_400_000).toISOString() : null;
  return { trialEndsAt: ends, machineLimit: ends ? 5 : null, trialRestoreUntil: restore };
}

/** setTrialEnd prorroga (data) ou converte em cliente (null) uma organização em teste. */
export function setTrialEnd(orgId: string, endsAt: string | null) {
  if (endsAt === null) trialEnds.delete(orgId);
  else trialEnds.set(orgId, endsAt);
}

/** renameOrg aplica a edição na simulação. */
export function renameOrg(orgId: string, name: string): T.Organization | undefined {
  const p = ORGS.find((o) => o.id === orgId);
  if (!p) return undefined;
  p.name = name;
  const d = orgData(orgId);
  if (d) d.profile.name = name;
  return { id: p.id, name, kind: p.kind, parentId: p.parentId, machineCount: d?.machines.length ?? 0,
    protectedBytes: d?.usage.protectedBytes ?? 0, storedBytes: d?.usage.storedBytes ?? 0,
    quotaBytes: newOrgQuota.has(orgId) ? newOrgQuota.get(orgId)! : (d?.usage.quotaBytes ?? 0), status: "ok", ...closureOf(orgId), ...trialOf(orgId) };
}

// Quota de armazenamento das organizações criadas na simulação (ADR 0037); nula é sem limite.
const newOrgQuota = new Map<string, number | null>();

// Encerramento (ADR 0024) na simulação: encerradas ficam com a data; a
// expurgada some da lista.
const closedOrgs = new Map<string, { closedAt: string; purgeAfter: string; purged: boolean }>();
const PURGE_GRACE = 30 * 86_400_000;

function closureOf(orgId: string): Pick<T.Organization, "closedAt" | "purgeAfter" | "purgeError"> {
  const c = closedOrgs.get(orgId);
  return { closedAt: c?.closedAt ?? null, purgeAfter: c?.purgeAfter ?? null, purgeError: "" };
}

/** closeOrg encerra; devolve null quando não existe, "closed" se já estava, "children" se é MSP com filhas. */
export function closeOrg(orgId: string, now: number): T.Organization | "closed" | "children" | null {
  const p = ORGS.find((o) => o.id === orgId);
  if (!p) return null;
  if (closedOrgs.get(orgId)) return "closed";
  if (ORGS.some((o) => o.parentId === orgId && !closedOrgs.get(o.id)?.purged)) return "children";
  closedOrgs.set(orgId, { closedAt: new Date(now).toISOString(), purgeAfter: new Date(now + PURGE_GRACE).toISOString(), purged: false });
  return organizations(now).find((o) => o.id === orgId) ?? null;
}

export function reopenOrg(orgId: string, now: number): T.Organization | "open" | null {
  const c = closedOrgs.get(orgId);
  if (!ORGS.some((o) => o.id === orgId) || c?.purged) return null;
  if (!c) return "open";
  closedOrgs.delete(orgId);
  return organizations(now).find((o) => o.id === orgId) ?? null;
}

/** purgeOrg antecipa o expurgo; a simulação apaga em alguns segundos, como o worker faria. */
export function purgeOrg(orgId: string, now: number): T.Organization | "open" | null {
  const c = closedOrgs.get(orgId);
  if (!ORGS.some((o) => o.id === orgId) || c?.purged) return null;
  if (!c) return "open";
  c.purgeAfter = new Date(now).toISOString();
  setTimeout(() => { c.purged = true; }, 3_000);
  return organizations(now).find((o) => o.id === orgId) ?? null;
}

// ---------------------------------------------------------------------------
// Armazenamento gerido pela plataforma (ADR 0021)
//
// Os pools existem uma vez, pertencem ao administrador da plataforma e são
// atribuídos às organizações com quota. O cliente só enxerga o que vem de
// storageSummary(): nome e volume, nunca backend, endereço ou id interno.

export const POOL_SP = "pool-sp01";
export const POOL_RIO = "pool-rio01";
export const POOL_CPS = "pool-cps01";

function buildPlatformPools(now: number): T.StoragePool[] {
  return [
    {
      id: POOL_SP, name: "Datacenter São Paulo", backend: "local",
      location: "zfs tank-sp01/ebackup", role: "primary", replicaPoolId: POOL_CPS,
      immutableDays: 14, usedBytes: 0, capacityBytes: 4 * TB, dedupRatio: 2.7, health: "healthy",
      lastCheckAt: iso(now - 38 * 60_000), replicationLagSeconds: null, lastError: null, editable: true, settings: {},
      detectedCapacityBytes: 3.94 * TB, detectedFreeBytes: 0.79 * TB, probedAt: iso(now - 6 * 3600_000),
      assignedQuotaBytes: null, orgCount: 0,
    },
    {
      id: POOL_RIO, name: "Nuvem Rio de Janeiro", backend: "s3",
      location: "s3.rio.exemplo.com.br / ebackup-rio", role: "primary", replicaPoolId: null,
      immutableDays: 1825, usedBytes: 0, capacityBytes: 4 * TB, dedupRatio: 2.4, health: "healthy",
      lastCheckAt: iso(now - 21 * 60_000), replicationLagSeconds: null, lastError: null, editable: true,
      settings: { s3: { endpoint: "s3.rio.exemplo.com.br", region: "br-rio-1", bucket: "ebackup-rio", prefix: "clientes", accessKeyId: "EBK0RIOEXEMPLO01" } },
      // S3 e compatíveis não informam capacidade: vale o número informado.
      detectedCapacityBytes: null, detectedFreeBytes: null, probedAt: iso(now - 26 * 3600_000),
      assignedQuotaBytes: null, orgCount: 0,
    },
    {
      id: POOL_CPS, name: "Réplica Campinas", backend: "sftp",
      location: "sftp://replica.dc2.interno/ebackup", role: "replica", replicaPoolId: null,
      immutableDays: null, usedBytes: 0, capacityBytes: 4 * TB, dedupRatio: 2.7, health: "degraded",
      lastCheckAt: iso(now - 12 * 60_000), replicationLagSeconds: 5 * 3600 + 20 * 60, lastError: null, editable: true,
      settings: { sftp: { host: "replica.dc2.interno", port: 22, username: "ebackup", path: "/ebackup", knownHosts: "replica.dc2.interno ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIExemploDeChaveDoServidorMock" } },
      detectedCapacityBytes: 3.94 * TB, detectedFreeBytes: 0.88 * TB, probedAt: iso(now - 6 * 3600_000),
      assignedQuotaBytes: null, orgCount: 0,
    },
  ];
}

let poolCache: T.StoragePool[] | null = null;

/** Pools da plataforma, com as mutações feitas pelo administrador nesta sessão. */
export function platformPools(now = Date.now()): T.StoragePool[] {
  if (!poolCache) poolCache = buildPlatformPools(now);
  return poolCache;
}

export function platformPool(poolId: string): T.StoragePool | undefined {
  return platformPools().find((p) => p.id === poolId);
}

/**
 * Fração da quota que a organização usa: nulo é sem limite e 1 ou mais põe o
 * cliente acima do contratado, para a console mostrar o aviso e o bloqueio.
 */
const ASSIGN_PLAN: { orgId: string; poolId: string; fill: number | null }[] = [
  { orgId: "msp-nuvemserra", poolId: POOL_SP, fill: 0.34 },
  { orgId: "org-serraazul", poolId: POOL_SP, fill: 0.92 },
  { orgId: "org-vidaplena", poolId: POOL_SP, fill: 1 },
  { orgId: "org-horizonte", poolId: POOL_RIO, fill: null },
  { orgId: "org-mouralima", poolId: POOL_RIO, fill: 0.58 },
];

export interface Assignment {
  orgId: string;
  poolId: string;
  quotaBytes: number | null;
  state: "active" | "draining";
}

/** Quota de faixa comercial (múltiplo de 100 GB) que dá o uso pretendido. */
function quotaFor(used: number, fill: number): number {
  const step = 100 * GB;
  if (fill >= 1) {
    // Acima do contratado: a faixa fica na primeira marca abaixo do usado.
    const below = Math.floor(used / step) * step;
    return Math.max(step, below < used ? below : below - step);
  }
  const q = Math.max(step, Math.round(used / fill / step) * step);
  return q <= used ? q + step : q;
}

let assignCache: Assignment[] | null = null;

export function assignments(): Assignment[] {
  if (!assignCache) {
    assignCache = ASSIGN_PLAN.map((a) => ({
      orgId: a.orgId,
      poolId: a.poolId,
      quotaBytes: a.fill === null ? null : quotaFor(orgData(a.orgId)?.usage.storedBytes ?? 0, a.fill),
      state: "active" as const,
    }));
  }
  return assignCache;
}

const orgAssignments = (orgId: string) => assignments().filter((a) => a.orgId === orgId);

/**
 * Uso da organização no pool. Como cada organização começa com um pool só, o
 * primeiro atribuído guarda tudo; um pool atribuído depois começa vazio.
 */
export function usedOf(orgId: string, poolId: string): number {
  const rows = orgAssignments(orgId);
  if (rows[0]?.poolId !== poolId) return 0;
  return orgData(orgId)?.usage.storedBytes ?? 0;
}

function repositoriesOf(orgId: string, poolId: string): number {
  const rows = orgAssignments(orgId);
  if (rows[0]?.poolId !== poolId) return 0;
  return orgData(orgId)?.machines.filter((m) => m.status !== "never").length ?? 0;
}

/** Atribuições da organização como a API do administrador devolve. */
export function orgPools(orgId: string): T.OrgPool[] {
  return orgAssignments(orgId).map((a) => ({
    poolId: a.poolId,
    poolName: platformPool(a.poolId)?.name ?? a.poolId,
    quotaBytes: a.quotaBytes,
    usedBytes: usedOf(orgId, a.poolId),
    state: a.state,
    repositories: repositoriesOf(orgId, a.poolId),
  }));
}

/** Uso, quota somada e número de organizações de cada pool da plataforma. */
export function poolTotals(poolId: string): { usedBytes: number; assignedQuotaBytes: number | null; orgCount: number } {
  const pool = platformPool(poolId);
  if (pool?.role === "replica") {
    // A réplica guarda uma cópia do que os primários que apontam para ela têm.
    const used = platformPools()
      .filter((p) => p.replicaPoolId === poolId && p.id !== poolId)
      .reduce((a, p) => a + poolTotals(p.id).usedBytes, 0);
    return { usedBytes: Math.round(used * 0.97), assignedQuotaBytes: null, orgCount: 0 };
  }
  const rows = assignments().filter((a) => a.poolId === poolId && a.state === "active");
  const unlimited = rows.some((a) => a.quotaBytes === null);
  return {
    usedBytes: rows.reduce((a, r) => a + usedOf(r.orgId, poolId), 0),
    assignedQuotaBytes: unlimited ? null : rows.reduce((a, r) => a + (r.quotaBytes ?? 0), 0),
    orgCount: rows.length,
  };
}

/** Identificador opaco da área: derivado de organização e pool, sem significado fora dali. */
const areaId = (orgId: string, poolId: string) => `area-${hashString(orgId + "|" + poolId).toString(16).padStart(8, "0")}`;

/** Armazenamento como o cliente vê (ADR 0021): volume, sem infraestrutura. */
export function storageSummary(orgId: string): T.StorageSummary {
  const areas: T.StorageArea[] = orgAssignments(orgId)
    .filter((a) => a.state === "active")
    .map((a) => {
      const used = usedOf(orgId, a.poolId);
      return {
        id: areaId(orgId, a.poolId),
        name: platformPool(a.poolId)?.name ?? "—",
        quotaBytes: a.quotaBytes,
        usedBytes: used,
        freeBytes: a.quotaBytes === null ? null : Math.max(0, a.quotaBytes - used),
        replicated: platformPool(a.poolId)?.replicaPoolId != null,
      };
    });
  const unlimited = areas.some((a) => a.quotaBytes === null);
  const quota = unlimited ? null : areas.reduce((acc, a) => acc + (a.quotaBytes ?? 0), 0);
  const used = areas.reduce((acc, a) => acc + a.usedBytes, 0);
  return { quotaBytes: quota, usedBytes: used, freeBytes: quota === null ? null : Math.max(0, quota - used), pools: areas };
}

// ---------------------------------------------------------------------------
// Estado por organização

export interface OrgData {
  profile: OrgProfile;
  groups: T.MachineGroup[];
  policies: T.Policy[];
  pools: T.StoragePool[];
  machines: T.MachineDetail[];
  snapshots: Map<string, T.Snapshot[]>;
  /** Discos por máquina para o backup de imagem (ADR 0022); macOS não tem. */
  disks: Map<string, T.MachineDisks>;
  /** Instâncias de banco cadastradas e motores achados, por máquina (ADR 0023). */
  databases: Map<string, T.DatabaseInstance[]>;
  foundDatabases: Map<string, T.FoundDatabases>;
  /** Catálogo de credenciais e compartilhamentos por máquina (ADR 0030). */
  credentials: T.Credential[];
  shares: Map<string, T.Share[]>;
  jobs: T.Job[];
  usage: T.Usage;
  tokens: T.EnrollmentToken[];
  audit: T.AuditEvent[];
  users: T.User[];
  apiTokens: T.ApiToken[];
  invitations: T.Invitation[];
  alerts: T.Alert[];
  channels: T.NotificationChannel[];
}

type Kind = "server" | "pc" | "notebook" | "mac";

interface Spec {
  hostname: string;
  kind: Kind;
  os: T.OS;
  osVersion: string;
  forced?: { status: T.ProtectionStatus; error: string | null };
}

const DEPTS = ["FIN", "RH", "LOG", "COM", "OPS", "DIR", "TI", "FROTA", "COMPRAS", "JUR"];

function specsFor(p: OrgProfile, rng: () => number): Spec[] {
  const pre = p.prefix;
  const low = pre.toLowerCase();
  const specs: Spec[] = [];

  if (p.kind === "msp") {
    specs.push(
      { hostname: `srv-console-01`, kind: "server", os: "linux", osVersion: "Ubuntu 24.04 LTS" },
      { hostname: `srv-monitor-01`, kind: "server", os: "linux", osVersion: "Debian 13" },
      { hostname: `NS-SUPORTE-NB01`, kind: "notebook", os: "windows", osVersion: "Windows 11 Pro 25H2" },
      { hostname: `NS-SUPORTE-NB02`, kind: "notebook", os: "windows", osVersion: "Windows 11 Pro 25H2" },
      { hostname: `mac-dir-01`, kind: "mac", os: "macos", osVersion: "macOS 26.0 Tahoe" },
      { hostname: `NS-ADM-PC01`, kind: "pc", os: "windows", osVersion: "Windows 11 Pro 24H2" },
    );
    return specs;
  }

  specs.push(
    {
      hostname: `srv-arquivos-${low}01`, kind: "server", os: "windows", osVersion: "Windows Server 2022",
      forced: { status: "failed", error: "VSS: o gravador SqlServerWriter está em estado de falha (0x800423f4). O volume D: não foi lido." },
    },
    { hostname: `srv-erp-${low}01`, kind: "server", os: "windows", osVersion: "Windows Server 2025" },
    { hostname: `srv-db-${low}01`, kind: "server", os: "linux", osVersion: "Ubuntu 24.04 LTS" },
  );
  if (p.id === "org-serraazul") {
    specs.push(
      { hostname: "srv-tms-01", kind: "server", os: "linux", osVersion: "Debian 13" },
      {
        hostname: "srv-rastreio-01", kind: "server", os: "linux", osVersion: "Ubuntu 22.04 LTS",
        forced: { status: "failed", error: "Snapshot LVM falhou: espaço livre insuficiente no grupo de volumes vg0 (2,1 GB livres, mínimo 5 GB)." },
      },
      {
        hostname: "mac-mkt-02", kind: "mac", os: "macos", osVersion: "macOS 26.0 Tahoe",
        forced: { status: "never", error: "Acesso Total ao Disco não concedido ao agente. Conceda em Ajustes do Sistema › Privacidade e Segurança." },
      },
      {
        hostname: "SA-LOG-NB07", kind: "notebook", os: "windows", osVersion: "Windows 11 Pro 25H2",
        forced: { status: "stale", error: "Sem contato há 4 dias. Notebook provavelmente desligado ou fora da rede." },
      },
      {
        hostname: "SA-FIN-PC01-antigo", kind: "pc", os: "windows", osVersion: "Windows 10 Pro 22H2",
        forced: { status: "revoked", error: null },
      },
      {
        hostname: "SA-COM-NB03", kind: "notebook", os: "windows", osVersion: "Windows 11 Pro 24H2",
        forced: { status: "stale", error: "Último backup há 3 dias. Janela de backup encerrada antes da conclusão (limite de 20 Mbps)." },
      },
    );
  }

  while (specs.length < p.machines) {
    const dept = DEPTS[Math.floor(rng() * DEPTS.length)] ?? "ADM";
    const r = rng();
    const n = String(Math.floor(rng() * 30) + 1).padStart(2, "0");
    let spec: Spec;
    if (r < 0.5) spec = { hostname: `${pre}-${dept}-PC${n}`, kind: "pc", os: "windows", osVersion: rng() < 0.7 ? "Windows 11 Pro 24H2" : "Windows 11 Pro 25H2" };
    else if (r < 0.85) spec = { hostname: `${pre}-${dept}-NB${n}`, kind: "notebook", os: "windows", osVersion: "Windows 11 Pro 25H2" };
    else spec = { hostname: `mac-${dept.toLowerCase()}-${n}`, kind: "mac", os: "macos", osVersion: rng() < 0.6 ? "macOS 26.0 Tahoe" : "macOS 15.6 Sequoia" };
    if (!specs.some((s) => s.hostname === spec.hostname)) specs.push(spec);
  }
  return specs;
}

function groupsFor(org: string): T.MachineGroup[] {
  return [
    { id: `${org}:g-servidores`, name: "Servidores", machineCount: 0 },
    { id: `${org}:g-estacoes`, name: "Estações de trabalho", machineCount: 0 },
    { id: `${org}:g-notebooks`, name: "Notebooks", machineCount: 0 },
    { id: `${org}:g-mac`, name: "Mac", machineCount: 0 },
  ];
}

/**
 * Pools que a organização usa: os da plataforma atribuídos a ela, mais a
 * réplica do primário. São cópias, porque o uso é contado por organização.
 */
function poolsFor(p: OrgProfile, now: number): T.StoragePool[] {
  const assigned = ASSIGN_PLAN.filter((a) => a.orgId === p.id).map((a) => a.poolId);
  const pools = buildPlatformPools(now).filter((x) => assigned.includes(x.id));
  for (const primary of [...pools]) {
    const replica = primary.replicaPoolId && buildPlatformPools(now).find((x) => x.id === primary.replicaPoolId);
    if (replica && !pools.some((x) => x.id === replica.id)) pools.push(replica);
  }
  // O cliente não altera pool nenhum desde o ADR 0021.
  for (const x of pools) x.editable = false;
  return pools;
}

function policiesFor(p: OrgProfile, now: number): T.Policy[] {
  const primary = ASSIGN_PLAN.find((a) => a.orgId === p.id)?.poolId ?? POOL_SP;
  const list: T.Policy[] = [
    {
      id: `${p.id}:pol-servidores`, name: "Servidores", machineCount: 0, updatedAt: iso(now - 9 * DAY),
      include: ["D:\\Dados", "C:\\ProgramData\\ERP", "/srv", "/etc", "/var/lib/postgresql"],
      exclude: ["*.tmp", "pagefile.sys", "/var/lib/postgresql/*/pg_wal"],
      schedule: { frequency: "hourly", time: "00:15", weekdays: [0, 1, 2, 3, 4, 5, 6], windowHours: 24 },
      retention: { daily: 30, weekly: 12, monthly: 24, yearly: 5 },
      poolId: primary, rpoHours: 4, osSnapshot: true, bandwidthLimitMbps: null,
      image: { enabled: true, disks: [], exclude: {} },
    },
    {
      id: `${p.id}:pol-estacoes`, name: "Estações de trabalho", machineCount: 0, updatedAt: iso(now - 2 * DAY),
      include: ["C:\\Users", "/Users"],
      exclude: ["**/AppData/Local/Temp", "**/Downloads/*.iso", "**/Library/Caches", "*.tmp", "~$*"],
      schedule: { frequency: "daily", time: "12:30", weekdays: [1, 2, 3, 4, 5], windowHours: 4 },
      retention: { daily: 14, weekly: 8, monthly: 12, yearly: 0 },
      poolId: primary, rpoHours: 26, osSnapshot: true, bandwidthLimitMbps: null,
      image: { enabled: false, disks: [], exclude: {} },
    },
    {
      id: `${p.id}:pol-notebooks`, name: "Notebooks (fora do escritório)", machineCount: 0, updatedAt: iso(now - 21 * DAY),
      include: ["C:\\Users", "/Users"],
      exclude: ["**/AppData/Local/Temp", "**/OneDrive", "*.tmp"],
      schedule: { frequency: "daily", time: "19:00", weekdays: [0, 1, 2, 3, 4, 5, 6], windowHours: 6 },
      retention: { daily: 14, weekly: 8, monthly: 6, yearly: 0 },
      poolId: primary, rpoHours: 48, osSnapshot: true, bandwidthLimitMbps: 20,
      image: { enabled: false, disks: [], exclude: {} },
    },
  ];
  if (p.fiscalArchive) {
    list.push({
      id: `${p.id}:pol-fiscal`, name: "Arquivo fiscal (5 anos, imutável)", machineCount: 0, updatedAt: iso(now - 60 * DAY),
      include: ["D:\\Dados\\Fiscal", "D:\\Dados\\Contabilidade"],
      exclude: [],
      schedule: { frequency: "weekly", time: "02:00", weekdays: [0], windowHours: 8 },
      retention: { daily: 0, weekly: 8, monthly: 60, yearly: 5 },
      poolId: primary, rpoHours: 192, osSnapshot: true, bandwidthLimitMbps: 100,
      image: { enabled: false, disks: [], exclude: {} },
    });
  }
  return list;
}

// ---------------------------------------------------------------------------

function restorePointsFor(status: T.ProtectionStatus, kind: Kind, rng: () => number, today: number): T.RestorePointDay[] {
  const days: T.RestorePointDay[] = [];
  const staleDays = 3 + Math.floor(rng() * 3);
  const failedDays = 1 + Math.floor(rng() * 2);
  const revokedAt = 9 + Math.floor(rng() * 6);
  for (let i = 29; i >= 0; i--) {
    const t = today - i * DAY;
    const dow = new Date(t).getDay();
    const weekend = dow === 0 || dow === 6;
    let state: T.RestorePointDay["state"] = "ok";
    const r = rng();
    if (kind !== "server" && weekend && r < 0.75) state = "none";
    else if (r < 0.03) state = "failed";
    else if (r < 0.07) state = "partial";

    if (status === "never") state = "none";
    if (status === "stale" && i < staleDays) state = "none";
    if (status === "failed" && i < failedDays) state = "failed";
    if (status === "revoked" && i < revokedAt) state = "none";
    if (status === "protected" && i === 0) state = "ok";
    days.push({ date: isoDate(t + 12 * 3600_000), state });
  }
  return days;
}

function sizeFor(kind: Kind, rng: () => number): number {
  const ranges: Record<Kind, [number, number]> = {
    server: [160, 820], pc: [18, 110], notebook: [14, 70], mac: [60, 240],
  };
  const [lo, hi] = ranges[kind];
  return Math.round((lo + rng() * (hi - lo)) * GB);
}

function buildOrg(p: OrgProfile, now: number): OrgData {
  const rng = mulberry32(hashString(p.id));
  const today = startOfToday(now);
  const groups = groupsFor(p.id);
  const pools = poolsFor(p, now);
  const policies = policiesFor(p, now);
  const specs = specsFor(p, rng);

  const machines: T.MachineDetail[] = specs.map((s, idx) => {
    let status: T.ProtectionStatus = "protected";
    let lastError: string | null = null;
    if (s.forced) {
      status = s.forced.status;
      lastError = s.forced.error;
    } else if (p.kind === "customer" && p.id !== "org-serraazul") {
      const r = rng();
      if (idx === 3 && p.id === "org-horizonte") {
        status = "failed";
        lastError = "Disco de origem com setores ilegíveis: 14 arquivos em C:\\Users\\secretaria não puderam ser lidos.";
      } else if (r < 0.06) {
        status = "stale";
        lastError = "Sem contato há 3 dias.";
      }
    }
    const groupKey = s.kind === "server" ? "g-servidores" : s.kind === "pc" ? "g-estacoes" : s.kind === "notebook" ? "g-notebooks" : "g-mac";
    const policyKey = s.kind === "server" ? "pol-servidores" : s.kind === "notebook" ? "pol-notebooks" : "pol-estacoes";
    const restorePoints = restorePointsFor(status, s.kind, rng, today);
    const lastOk = [...restorePoints].reverse().find((d) => d.state === "ok" || d.state === "partial");
    const size = sizeFor(s.kind, rng);
    const dedup = 2.1 + rng() * 1.3;
    const hour = s.kind === "server" ? now - (20 + Math.floor(rng() * 40)) * 60_000 : 0;
    const lastSuccessAt =
      status === "never" || !lastOk
        ? null
        : s.kind === "server" && status === "protected"
          ? iso(hour)
          : iso(new Date(lastOk.date + "T" + (s.kind === "notebook" ? "19" : "12") + ":4" + Math.floor(rng() * 10) + ":00").getTime());
    const online = status === "revoked" ? false : status === "stale" ? false : rng() > 0.12;
    const lastSeenAt =
      status === "stale" ? iso(now - (3 + Math.floor(rng() * 2)) * DAY) : status === "revoked" ? iso(now - 11 * DAY) : iso(now - Math.floor(rng() * (online ? 90 : 900)) * 1000);
    const osArch = s.os === "macos" ? "arm64" : "amd64";
    const sources =
      s.os === "windows" ? (s.kind === "server" ? ["D:\\Dados", "C:\\ProgramData\\ERP"] : ["C:\\Users"]) : s.os === "macos" ? ["/Users"] : ["/srv", "/etc", "/var/lib/postgresql"];
    const enrolledDaysAgo = status === "never" ? 1 : 40 + Math.floor(rng() * 300);
    return {
      id: `${p.id}:m-${hex(rng, 10)}`,
      hostname: s.hostname,
      os: s.os,
      osVersion: s.osVersion,
      arch: osArch,
      agentVersion: rng() < 0.85 ? "2026.09.1" : "2026.08.3",
      groupId: `${p.id}:${groupKey}`,
      policyId: `${p.id}:${policyKey}`,
      status,
      online,
      lastSeenAt,
      lastSuccessAt,
      protectedBytes: status === "never" ? 0 : size,
      storedBytes: status === "never" ? 0 : Math.round(size / dedup),
      restorePoints,
      enrolledAt: iso(now - enrolledDaysAgo * DAY),
      ipAddress: `10.${20 + (hashString(p.id) % 30)}.${Math.floor(rng() * 8)}.${10 + Math.floor(rng() * 200)}`,
      sources,
      lastError,
      certificateNotAfter: iso(now + (20 + Math.floor(rng() * 60)) * DAY),
      imageDisks: [],
    };
  });

  for (const g of groups) g.machineCount = machines.filter((m) => m.groupId === g.id).length;
  for (const pol of policies) pol.machineCount = machines.filter((m) => m.policyId === pol.id).length;
  if (p.fiscalArchive) {
    const fiscal = policies.find((x) => x.id.endsWith("pol-fiscal"));
    if (fiscal) fiscal.machineCount = 1;
  }

  const protectedTotal = machines.reduce((a, m) => a + m.protectedBytes, 0);
  const storedTotal = machines.reduce((a, m) => a + m.storedBytes, 0);
  const primary = pools.find((x) => x.role === "primary");
  const replica = pools.find((x) => x.role === "replica");
  if (primary) {
    primary.usedBytes = storedTotal;
    primary.dedupRatio = storedTotal ? protectedTotal / storedTotal : 1;
  }
  if (replica) {
    replica.usedBytes = Math.round(storedTotal * 0.97);
    replica.dedupRatio = primary?.dedupRatio ?? 1;
  }

  // Franquia: próxima faixa de 500 GB acima de ~84% de uso.
  const quota = Math.max(500, Math.ceil(protectedTotal / 0.84 / GB / 500) * 500) * GB;

  const daily: T.UsageDay[] = [];
  for (let i = 29; i >= 0; i--) {
    const t = today - i * DAY;
    const growth = 1 - i * 0.0019 + (rng() - 0.5) * 0.004;
    const prot = Math.round(protectedTotal * growth);
    daily.push({ date: isoDate(t + 12 * 3600_000), protectedBytes: prot, storedBytes: Math.round(prot / (primary?.dedupRatio ?? 2.5)) });
  }
  const lastDay = daily[daily.length - 1];
  if (lastDay) {
    lastDay.protectedBytes = protectedTotal;
    lastDay.storedBytes = storedTotal;
  }

  const monthStart = new Date(today);
  monthStart.setDate(1);
  const monthEnd = new Date(monthStart);
  monthEnd.setMonth(monthEnd.getMonth() + 1, 0);

  const usage: T.Usage = {
    planName: `Volume ${quota / GB >= 1000 ? `${(quota / GB / 1000).toLocaleString("pt-BR")} TB` : `${quota / GB} GB`}`,
    quotaBytes: quota,
    protectedBytes: protectedTotal,
    storedBytes: storedTotal,
    overage: "charge",
    periodStart: isoDate(monthStart.getTime() + 12 * 3600_000),
    periodEnd: isoDate(monthEnd.getTime() + 12 * 3600_000),
    daily,
    topMachines: [...machines]
      .sort((a, b) => b.protectedBytes - a.protectedBytes)
      .slice(0, 6)
      .map((m) => ({ machineId: m.id, hostname: m.hostname, protectedBytes: m.protectedBytes })),
  };

  const snapshots = new Map<string, T.Snapshot[]>();
  const disks = new Map<string, T.MachineDisks>();
  const databases = new Map<string, T.DatabaseInstance[]>();
  const foundDatabases = new Map<string, T.FoundDatabases>();
  // Credencial do catálogo e um compartilhamento na primeira máquina servidor (ADR 0030).
  const credentials: T.Credential[] = [{
    id: `${p.id}:cred-nas`, name: "NAS Synology", kind: "smb", username: "svc-backup", domain: "EMPRESA", usedBy: 0,
    createdBy: "Rafaela Nunes", createdAt: iso(now - 40 * DAY), updatedAt: iso(now - 40 * DAY), lastUsedAt: iso(now - 7 * 3600_000),
  }];
  const shares = new Map<string, T.Share[]>();
  const bridge = machines.find((m) => m.hostname.startsWith("srv-") && m.status !== "revoked" && m.status !== "never");
  for (const m of machines) {
    disks.set(m.id, disksFor(m, now));
    const dbs = databasesFor(m, now);
    databases.set(m.id, dbs.instances);
    foundDatabases.set(m.id, dbs.found);
    const snaps = snapshotsFor(m, policies, pools, rng, now);
    snaps.push(...databaseSnapshotsFor(m, dbs.instances, pools, rng, now));
    if (m === bridge) {
      const list = sharesFor(m, credentials[0], now);
      shares.set(m.id, list);
      snaps.push(...shareSnapshotsFor(m, list, pools, rng, now));
    }
    snaps.sort((a, b) => +new Date(b.startedAt) - +new Date(a.startedAt));
    snapshots.set(m.id, snaps);
  }
  if (credentials[0]) credentials[0].usedBy = bridge ? 1 : 0;

  const jobs = jobsFor(p, machines, pools, rng, now);

  const users: T.User[] = [
    { id: "u-alan", name: "Alan Tavora", email: "alan@nuvemserra.com.br", role: "platform_admin", platformAdmin: true, totpEnabled: true, lastLoginAt: iso(now - 4 * 60_000) },
    { id: "u-rafaela", name: "Rafaela Nunes", email: "rafaela.nunes@nuvemserra.com.br", role: "org_admin", platformAdmin: false, totpEnabled: true, lastLoginAt: iso(now - 26 * 3600_000) },
    { id: "u-diego", name: "Diego Prates", email: "diego.prates@nuvemserra.com.br", role: "operator", platformAdmin: false, totpEnabled: true, lastLoginAt: iso(now - 3 * 3600_000) },
    { id: "u-contab", name: "Contabilidade Externa", email: "auditoria@contabilexemplo.com.br", role: "viewer", platformAdmin: false, totpEnabled: false, lastLoginAt: iso(now - 17 * DAY) },
  ];

  const tokens: T.EnrollmentToken[] = [
    {
      id: `${p.id}:tok-1`, label: "Notebooks comercial (lote setembro)", groupId: `${p.id}:g-notebooks`, policyId: `${p.id}:pol-notebooks`,
      createdAt: iso(now - 2 * DAY), expiresAt: iso(now + 5 * DAY), uses: 3, maxUses: 12, createdBy: "Rafaela Nunes",
    },
    {
      id: `${p.id}:tok-2`, label: "mac-mkt-02 (reinstalação)", groupId: `${p.id}:g-mac`, policyId: `${p.id}:pol-estacoes`,
      createdAt: iso(now - 26 * 3600_000), expiresAt: iso(now - 2 * 3600_000), uses: 1, maxUses: 1, createdBy: "Diego Prates",
    },
  ];

  const audit: T.AuditEvent[] = [
    { id: "a1", at: iso(now - 4 * 60_000), actor: "Alan Tavora", action: "Entrou na console com TOTP", actionCode: "", target: "—", ip: "177.92.14.203" },
    { id: "a2", at: iso(now - 3 * 3600_000), actor: "Diego Prates", action: "Iniciou restauração de 214 arquivos", actionCode: "", target: machines[6]?.hostname ?? "—", ip: "177.92.14.87" },
    { id: "a3", at: iso(now - 26 * 3600_000), actor: "Diego Prates", action: "Criou token de registro de uso único", actionCode: "", target: "mac-mkt-02 (reinstalação)", ip: "177.92.14.87" },
    { id: "a4", at: iso(now - 2 * DAY), actor: "Rafaela Nunes", action: "Alterou retenção diária de 7 para 14", actionCode: "", target: "Política Estações de trabalho", ip: "189.40.72.11" },
    { id: "a5", at: iso(now - 2 * DAY - 3600_000), actor: "Rafaela Nunes", action: "Criou token de registro para 12 máquinas", actionCode: "", target: "Notebooks comercial (lote setembro)", ip: "189.40.72.11" },
    { id: "a6", at: iso(now - 11 * DAY), actor: "Alan Tavora", action: "Revogou máquina", actionCode: "", target: "SA-FIN-PC01-antigo", ip: "177.92.14.203" },
    { id: "a7", at: iso(now - 12 * DAY), actor: "Integração RMM (token de API)", action: "Leu status de 32 máquinas", actionCode: "", target: "API /machines", ip: "34.95.180.4" },
    { id: "a8", at: iso(now - 60 * DAY), actor: "Alan Tavora", action: "Aumentou o armazenamento contratado", actionCode: "", target: p.name, ip: "177.92.14.203" },
  ];

  const apiTokens: T.ApiToken[] = [
    { id: "t1", name: "Integração RMM", prefix: "ebt1_7Q2mKd", role: "operator", createdBy: "Rafaela Nunes", createdAt: iso(now - 90 * DAY), lastUsedAt: iso(now - 9 * 60_000), expiresAt: iso(now + 275 * DAY) },
    { id: "t2", name: "Relatório mensal (script)", prefix: "ebt1_Kd81Zp", role: "viewer", createdBy: "Alan Tavora", createdAt: iso(now - 40 * DAY), lastUsedAt: iso(now - 14 * DAY), expiresAt: iso(now + 325 * DAY) },
  ];

  const invitations: T.Invitation[] = [
    { id: "i1", email: "joao.lima@nuvemserra.com.br", name: "João Lima", role: "operator", status: "pending", createdBy: "Rafaela Nunes", createdAt: iso(now - 5 * 3600_000), expiresAt: iso(now + 67 * 3600_000) },
  ];

  // Alertas de exemplo derivados das máquinas com problema.
  const alerts: T.Alert[] = machines.flatMap((m): T.Alert[] => {
    if (m.status === "failed") {
      return [{ id: `${p.id}:al-${m.id}`, code: "alert.backup_failed", severity: "critical", status: "open",
        message: `O último backup de ${m.hostname} falhou. ${m.lastError ?? ""}`.trim(), args: { hostname: m.hostname, machineId: m.id },
        openedAt: iso(now - 50 * 60_000), resolvedAt: null }];
    }
    if (m.status === "stale") {
      return [{ id: `${p.id}:al-${m.id}`, code: "alert.machine_stale", severity: "warning", status: "open",
        message: `${m.hostname} está sem backup concluído há 96 horas (RPO da política: 26 h)`, args: { hostname: m.hostname, machineId: m.id, hours: "96", rpo: "26" },
        openedAt: iso(now - 70 * 3600_000), resolvedAt: null }];
    }
    return [];
  });

  const channels: T.NotificationChannel[] = [
    { id: `${p.id}:ch-1`, kind: "email", name: "Equipe de suporte", emails: ["suporte@nuvemserra.com.br", "plantao@nuvemserra.com.br"], url: null,
      minSeverity: "warning", locale: "pt-BR", enabled: true, monthlySummary: true, createdAt: iso(now - 20 * DAY), lastDeliveryAt: iso(now - 50 * 60_000), lastError: null },
  ];

  return { profile: p, groups, policies, pools, machines, snapshots, disks, databases, foundDatabases, credentials, shares, jobs, usage, tokens, audit, users, apiTokens, invitations, alerts, channels };
}

/** Um compartilhamento SMB copiado pela máquina ponte, com o último teste aprovado (ADR 0030). */
function sharesFor(m: T.MachineDetail, cred: T.Credential | undefined, now: number): T.Share[] {
  const created = iso(new Date(m.enrolledAt).getTime() + 2 * DAY);
  return [{
    id: `${m.id}:share-docs`, name: "Documentos do NAS", host: "nas01.empresa.local", share: "documentos", path: "Departamentos",
    unc: "\\\\nas01.empresa.local\\documentos\\Departamentos", credentialId: cred?.id ?? "", exclude: ["*.tmp", "~$*", "Thumbs.db"],
    schedule: { frequency: "daily", time: "22:30", weekdays: [1, 2, 3, 4, 5], windowHours: 6 }, enabled: true, activeJob: "",
    createdAt: created, updatedAt: created,
    lastTest: {
      ok: true, code: "share.test.ok", message: "Conexão, credencial e pasta estão certas (SMB 3.1.1).", dialect: "3.1.1", signed: true, encrypted: false,
      entries: 18_420, bytes: Math.round(96.3 * GB), warnings: [], at: iso(now - 2 * DAY),
      steps: [
        { code: "share.step.resolve", message: "Resolvendo o nome nas01.empresa.local", status: "ok" },
        { code: "share.step.connect", message: "Conectando a nas01.empresa.local pela porta 445", status: "ok" },
        { code: "share.step.auth", message: "Autenticando como svc-backup", status: "ok" },
        { code: "share.step.mount", message: "Abrindo o compartilhamento documentos", status: "ok" },
        { code: "share.step.list", message: "Listando a pasta Departamentos", status: "ok" },
        { code: "share.step.estimate", message: "Estimando o volume: 18.420 itens, 96,3 GB", status: "ok" },
      ],
    },
  }];
}

/** Pontos de restauração do compartilhamento: um por noite, como arquivos comuns. */
function shareSnapshotsFor(m: T.MachineDetail, list: T.Share[], pools: T.StoragePool[], rng: () => number, now: number): T.Snapshot[] {
  const pool = pools.find((x) => x.role === "primary");
  const out: T.Snapshot[] = [];
  for (const sh of list) {
    for (const age of [0, 1, 2]) {
      const at = now - age * DAY - 2 * 3600_000;
      if (new Date(m.enrolledAt).getTime() > at) continue;
      const total = Math.round(96.3 * GB * (1 - age * 0.004));
      out.push({
        id: hex(rng, 32), machineId: m.id, sourcePath: sh.unc, startedAt: iso(at), endedAt: iso(at + 3_100_000), status: "complete",
        totalBytes: total, addedBytes: Math.round(total * (age === 2 ? 1 : 0.012)), fileCount: 18_400 + age,
        retention: age === 0 ? ["latest", "daily"] : ["daily"], poolId: pool?.id ?? "", immutableUntil: null, trigger: "schedule",
        kind: "share", volumes: [], databaseId: null, databases: [], shareId: sh.id, errors: [], errorTotal: 0, errorGroups: [], catalog: "done",
      });
    }
  }
  return out;
}

function snapshotsFor(m: T.MachineDetail, policies: T.Policy[], pools: T.StoragePool[], rng: () => number, now: number): T.Snapshot[] {
  const policy = policies.find((p) => p.id === m.policyId);
  const pool = pools.find((p) => p.id === policy?.poolId);
  const out: T.Snapshot[] = [];
  const days = m.restorePoints.map((d, i) => ({ ...d, i })).filter((d) => d.state === "ok" || d.state === "partial").reverse();
  let total = m.protectedBytes;
  days.forEach((d, n) => {
    const hour = policy?.schedule.time.split(":").map(Number) ?? [12, 30];
    const start = new Date(`${d.date}T00:00:00`);
    start.setHours(hour[0] ?? 12, (hour[1] ?? 30) + Math.floor(rng() * 9));
    const startMs = Math.min(start.getTime(), now - 30 * 60_000);
    const dur = (m.os === "windows" && m.protectedBytes > 150 * GB ? 1400 : 260) + Math.floor(rng() * 500);
    const added = Math.round(total * (0.002 + rng() * 0.012));
    const dow = new Date(startMs).getDay();
    const retention: T.Snapshot["retention"] = [];
    if (n === 0) retention.push("latest");
    if (n < (policy?.retention.daily ?? 14)) retention.push("daily");
    if (dow === 0) retention.push("weekly");
    if (new Date(startMs).getDate() === 1) retention.push("monthly");
    const immutable = pool?.immutableDays ? startMs + pool.immutableDays * DAY : null;
    out.push({
      id: hex(rng, 32),
      machineId: m.id,
      sourcePath: m.sources[0] ?? "/",
      startedAt: iso(startMs),
      endedAt: iso(startMs + dur * 1000),
      status: d.state === "partial" ? "partial" : "complete",
      totalBytes: total,
      addedBytes: added,
      fileCount: Math.round(total / (m.os === "linux" ? 180_000 : 420_000)),
      retention: retention.length ? retention : ["daily"],
      poolId: pool?.id ?? "",
      immutableUntil: immutable && immutable > now ? iso(immutable) : null,
      trigger: n === 2 ? "manual" : "schedule",
      kind: "files",
      volumes: [],
      databaseId: null,
      databases: [],
      shareId: null, errors: [], errorTotal: 0, errorGroups: [], catalog: "done",
    });
    total = Math.round(total - added * 0.8);
  });
  // Mensais mais antigos (retenção GFS).
  for (let k = 1; k <= 5; k++) {
    const d = new Date(now);
    d.setMonth(d.getMonth() - k, 1);
    d.setHours(12, 34, 0, 0);
    if (m.status === "never" || new Date(m.enrolledAt).getTime() > d.getTime()) break;
    out.push({
      id: hex(rng, 32), machineId: m.id, sourcePath: m.sources[0] ?? "/", startedAt: iso(d.getTime()), endedAt: iso(d.getTime() + 612_000), status: "complete",
      totalBytes: Math.round(m.protectedBytes * (1 - k * 0.04)), addedBytes: Math.round(m.protectedBytes * 0.006),
      fileCount: Math.round((m.protectedBytes * (1 - k * 0.04)) / 420_000), retention: ["monthly"], poolId: pool?.id ?? "",
      immutableUntil: null, trigger: "schedule", kind: "files", volumes: [], databaseId: null, databases: [], shareId: null, errors: [], errorTotal: 0, errorGroups: [], catalog: "done",
    });
  }
  // Pontos de imagem de disco nas máquinas que suportam (ADR 0022).
  const d = disksFor(m, now);
  for (let k = 0; k < d.disks.length && m.status !== "never"; k++) {
    const disk = d.disks[k];
    if (!disk) break;
    for (const age of [0, 1]) {
      const at = now - age * DAY - 5 * 3600_000;
      if (new Date(m.enrolledAt).getTime() > at) continue;
      out.push({
        id: hex(rng, 32), machineId: m.id, sourcePath: disk.id, startedAt: iso(at), endedAt: iso(at + 3_900_000), status: "complete",
        totalBytes: disk.sizeBytes, addedBytes: Math.round(disk.sizeBytes * (age === 0 ? 0.004 : 0.9)),
        fileCount: 0, retention: age === 0 ? ["latest", "daily"] : ["daily"], poolId: pool?.id ?? "",
        immutableUntil: null, trigger: "schedule", kind: "image",
        volumes: (disk.volumes ?? []).map((v) => ({
          number: v.number, name: v.name, mount: v.mount, label: v.label, filesystem: v.filesystem,
          sizeBytes: v.sizeBytes, usedBytes: v.usedBytes,
          consistency: m.os === "windows" ? "vss" : "crash",
        })),
        databaseId: null,
        databases: [],
        shareId: null, errors: [], errorTotal: 0, errorGroups: [], catalog: "none",
      });
    }
  }
  out.sort((a, b) => +new Date(b.startedAt) - +new Date(a.startedAt));
  return out;
}

/** Bancos de dados de demonstração: o servidor de banco tem PostgreSQL por
 *  socket e um MySQL em cluster InnoDB com senha; o ERP Windows tem um motor
 *  achado e ainda não cadastrado (ADR 0023). */
function databasesFor(m: T.MachineDetail, now: number): { instances: T.DatabaseInstance[]; found: T.FoundDatabases } {
  const none = { instances: [], found: { found: [], updatedAt: null } };
  if (m.status === "never" || m.status === "revoked") return none;
  const updatedAt = iso(now - 20 * 60_000);
  if (m.hostname.startsWith("srv-erp")) {
    return {
      instances: [],
      found: {
        found: [
          { engine: "sqlserver", version: "16.0", detail: "MSSQLSERVER" },
          { engine: "oracle", version: "19", sid: "ORCL", osUser: "NT AUTHORITY\\SYSTEM", detail: "C:\\app\\oracle\\product\\19.0.0\\dbhome_1" },
        ],
        updatedAt,
      },
    };
  }
  if (!m.hostname.startsWith("srv-db")) return none;
  const created = iso(new Date(m.enrolledAt).getTime() + DAY);
  const test = (over: Partial<T.DatabaseTest>): T.DatabaseTest => ({
    ok: true, code: "db.test.ok", message: "Conexão, ferramenta e permissões estão certas.", version: "", flavor: "", tool: "", toolVersion: "",
    role: "", databases: [], warnings: [], steps: [], at: iso(now - 3 * DAY), ...over,
  });
  const instances: T.DatabaseInstance[] = [
    {
      id: `${m.id}:db-erp`, name: "ERP (PostgreSQL)", engine: "postgresql", host: "", port: 5432, socket: "/var/run/postgresql", auth: "os", user: "",
      osUser: "postgres", databases: [], all: true, schedule: null, enabled: true, hasSecret: false, activeJob: "", createdAt: created, updatedAt: created,
      sid: "", mode: "", stagingDir: "", deleteArchivedLogs: false,
      lastTest: test({
        version: "16.4", tool: "pg_dump", toolVersion: "16.4",
        databases: [{ name: "erp", sizeBytes: Math.round(38.2 * GB) }, { name: "fiscal", sizeBytes: Math.round(6.4 * GB) }],
        steps: [
          { code: "db.step.tool_found", message: "Ferramenta pg_dump 16.4 encontrada.", status: "ok" },
          { code: "db.step.connected", message: "Conectado: PostgreSQL 16.4.", status: "ok" },
          { code: "db.step.databases", message: "2 bancos vistos.", status: "ok" },
        ],
      }),
    },
    {
      id: `${m.id}:db-loja`, name: "Loja virtual (MySQL)", engine: "mysql", host: "10.20.4.12", port: 3306, socket: "", auth: "password", user: "backup",
      osUser: "", databases: ["loja", "estoque"], all: false, schedule: { frequency: "daily", time: "01:30", weekdays: [0, 1, 2, 3, 4, 5, 6], windowHours: 3 },
      enabled: true, hasSecret: true, activeJob: "", createdAt: created, updatedAt: created, sid: "", mode: "", stagingDir: "", deleteArchivedLogs: false,
      lastTest: test({
        version: "8.0.39", flavor: "mysql", tool: "mysqldump", toolVersion: "8.0.39", role: "primary",
        databases: [{ name: "loja", sizeBytes: Math.round(12.8 * GB) }, { name: "estoque", sizeBytes: Math.round(2.1 * GB) }],
        warnings: [{ code: "db.warn.primary_node", message: "Este nó é o primário do cluster. Prefira copiar de um secundário para não pesar na produção." }],
        steps: [
          { code: "db.step.tool_found", message: "Ferramenta mysqldump 8.0.39 encontrada.", status: "ok" },
          { code: "db.step.connected", message: "Conectado: MySQL 8.0.39.", status: "ok" },
          { code: "db.step.databases", message: "2 bancos vistos.", status: "ok" },
          { code: "db.step.role", message: "Este nó é o primário do cluster.", status: "ok" },
          { code: "db.warn.primary_node", message: "Este nó é o primário do cluster. Prefira copiar de um secundário para não pesar na produção.", status: "warn" },
        ],
      }),
    },
  ];
  return {
    instances,
    found: {
      found: [
        { engine: "postgresql", version: "16", port: 5432, socket: "/var/run/postgresql", osUser: "postgres", detail: "/var/lib/postgresql/16/main" },
        { engine: "mysql", flavor: "mariadb", version: "10.11", port: 3307, socket: "/run/mysqld/mysqld.sock", osUser: "mysql", detail: "mariadb.service" },
      ],
      updatedAt,
    },
  };
}

/** Pontos de restauração dos bancos: um por noite, um arquivo por banco. */
function databaseSnapshotsFor(m: T.MachineDetail, instances: T.DatabaseInstance[], pools: T.StoragePool[], rng: () => number, now: number): T.Snapshot[] {
  const pool = pools.find((x) => x.role === "primary");
  const out: T.Snapshot[] = [];
  for (const inst of instances) {
    const pg = inst.engine === "postgresql";
    const dbs = inst.lastTest?.databases ?? [];
    for (const age of [0, 1, 2]) {
      const at = now - age * DAY - 7 * 3600_000;
      if (new Date(m.enrolledAt).getTime() > at) continue;
      const streams: T.SnapshotDatabase[] = dbs.map((d) => ({
        name: d.name, stream: `${d.name}.${pg ? "pgdump" : "sql"}`, format: pg ? "pg_custom" : "sql", sizeBytes: Math.round(d.sizeBytes * (0.55 + rng() * 0.05)),
      }));
      if (pg) streams.unshift({ name: "", stream: "globals.sql", format: "sql", sizeBytes: 18_432 });
      const total = streams.reduce((acc, x) => acc + x.sizeBytes, 0);
      out.push({
        id: hex(rng, 32), machineId: m.id, sourcePath: `db://${inst.engine}/${inst.id}`, startedAt: iso(at), endedAt: iso(at + 1_260_000), status: "complete",
        totalBytes: total, addedBytes: Math.round(total * (age === 2 ? 0.7 : 0.03)), fileCount: streams.length + 1,
        retention: age === 0 ? ["latest", "daily"] : ["daily"], poolId: pool?.id ?? "", immutableUntil: null, trigger: "schedule",
        kind: "database", volumes: [], databaseId: inst.id, databases: streams, shareId: null, errors: [], errorTotal: 0, errorGroups: [], catalog: "none",
      });
    }
  }
  return out;
}

/** Discos como o agente informaria: Windows e Linux têm; macOS não (ADR 0022). */
function disksFor(m: T.MachineDetail, now: number): T.MachineDisks {
  if (m.os === "macos" || m.status === "never") return { disks: [], updatedAt: null };
  const gb = (n: number) => n * GB;
  const updatedAt = iso(now - 20 * 60_000);
  if (m.os === "windows") {
    const size = Math.max(gb(120), Math.round(m.protectedBytes * 1.6));
    return {
      disks: [{
        id: "\\\\.\\PHYSICALDRIVE0", model: "Samsung PM9A1", sizeBytes: size, removable: false, system: true, scheme: "gpt",
        volumes: [
          { number: 1, name: "disco0-part1.img", mount: "", label: "Sistema EFI", filesystem: "fat32", offsetBytes: 1_048_576, sizeBytes: gb(0.1), usedBytes: gb(0.03) },
          { number: 2, name: "disco0-part2.img", mount: "C:", label: "Windows", filesystem: "ntfs", offsetBytes: gb(0.2), sizeBytes: size - gb(0.6), usedBytes: Math.round(m.protectedBytes * 0.8) },
          { number: 3, name: "disco0-part3.img", mount: "", label: "Recuperação", filesystem: "ntfs", offsetBytes: size - gb(0.4), sizeBytes: gb(0.4), usedBytes: gb(0.3) },
        ],
      }],
      updatedAt,
    };
  }
  const size = Math.max(gb(80), Math.round(m.protectedBytes * 1.5));
  return {
    disks: [{
      id: "/dev/sda", model: "Virtual disk", sizeBytes: size, removable: false, system: true, scheme: "gpt",
      volumes: [
        { number: 1, name: "disco0-part1.img", mount: "/boot/efi", label: "", filesystem: "fat32", offsetBytes: 1_048_576, sizeBytes: gb(0.5), usedBytes: gb(0.02) },
        { number: 2, name: "disco0-part2.img", mount: "/", label: "raiz", filesystem: "ext4", offsetBytes: gb(0.6), sizeBytes: size - gb(0.6), usedBytes: Math.round(m.protectedBytes * 0.9) },
      ],
    }],
    updatedAt,
  };
}

function jobsFor(p: OrgProfile, machines: T.MachineDetail[], pools: T.StoragePool[], rng: () => number, now: number): T.Job[] {
  const jobs: T.Job[] = [];
  let id = 0;
  const push = (j: Omit<T.Job, "id" | "messageCode" | "databaseId" | "shareId">) => jobs.push({ ...j, messageCode: "", databaseId: null, shareId: null, id: `${p.id}:job-${++id}` });

  const running = machines.find((m) => m.hostname.startsWith("srv-erp"));
  if (running) {
    push({ kind: "backup", status: "running", machineId: running.id, hostname: running.hostname, startedAt: iso(now - 7 * 60_000), endedAt: null, bytes: Math.round(running.protectedBytes * 0.004), progress: 0.62, message: "Enviando alterações de D:\\Dados (18.204 de 29.310 arquivos verificados)" });
  }
  const replica = pools.find((x) => x.role === "replica");
  if (replica) {
    push({
      kind: "replication", status: replica.health === "degraded" ? "warning" : "succeeded", machineId: null, hostname: null,
      startedAt: iso(now - 52 * 60_000), endedAt: iso(now - 31 * 60_000), bytes: Math.round(9.4 * GB), progress: null,
      message: replica.health === "degraded"
        ? "Réplica Campinas concluída com atraso: link de 200 Mbps saturado entre 18h e 23h."
        : "Réplica Campinas sincronizada: 9,4 GB copiados, integridade verificada.",
    });
  }
  for (const m of machines) {
    if (m.status === "failed") {
      push({ kind: "backup", status: "failed", machineId: m.id, hostname: m.hostname, startedAt: iso(now - (60 + rng() * 300) * 60_000), endedAt: iso(now - 50 * 60_000), bytes: 0, progress: null, message: m.lastError ?? "Falhou" });
    }
  }
  const restoreTarget = machines.find((m) => m.hostname.includes("FIN-PC")) ?? machines[6];
  if (restoreTarget) {
    push({ kind: "restore", status: "succeeded", machineId: restoreTarget.id, hostname: restoreTarget.hostname, startedAt: iso(now - 3 * 3600_000), endedAt: iso(now - 3 * 3600_000 + 190_000), bytes: Math.round(1.8 * GB), progress: null, message: "214 arquivos restaurados em C:\\Users\\financeiro\\Documents\\Fechamentos (manter ambos)" });
  }
  push({ kind: "maintenance", status: "succeeded", machineId: null, hostname: null, startedAt: iso(now - 5 * 3600_000), endedAt: iso(now - 4.6 * 3600_000), bytes: 0, progress: null, message: "Verificação de integridade: 12.483 packs conferidos, nenhum erro. 3,1 GB liberados pela retenção." });
  const newbie = machines.find((m) => m.status === "never");
  if (newbie) push({ kind: "enrollment", status: "succeeded", machineId: newbie.id, hostname: newbie.hostname, startedAt: iso(now - 26 * 3600_000), endedAt: iso(now - 26 * 3600_000), bytes: 0, progress: null, message: `${newbie.hostname} registrada com o token "${newbie.hostname} (reinstalação)"` });
  for (const m of machines.filter((x) => x.status === "protected").slice(0, 22)) {
    const t = m.lastSuccessAt ? new Date(m.lastSuccessAt).getTime() : now - 5 * 3600_000;
    const warn = rng() < 0.08;
    push({
      kind: "backup", status: warn ? "warning" : "succeeded", machineId: m.id, hostname: m.hostname,
      startedAt: iso(t - 600_000), endedAt: iso(t), bytes: Math.round(m.protectedBytes * (0.002 + rng() * 0.01)), progress: null,
      message: warn ? "3 arquivos em uso ignorados (PST do Outlook aberto)." : `${Math.round(m.protectedBytes / 420_000).toLocaleString("pt-BR")} arquivos verificados`,
    });
  }
  return jobs.sort((a, b) => {
    if (a.status === "running") return -1;
    if (b.status === "running") return 1;
    return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
  });
}

// ---------------------------------------------------------------------------

const cache = new Map<string, OrgData>();

export function orgData(orgId: string, now = Date.now()): OrgData | undefined {
  const existing = cache.get(orgId);
  if (existing) return existing;
  const profile = ORGS.find((o) => o.id === orgId);
  if (!profile) return undefined;
  const d = buildOrg(profile, now);
  cache.set(orgId, d);
  return d;
}

export function organizations(now = Date.now()): T.Organization[] {
  return ORGS.filter((p) => !closedOrgs.get(p.id)?.purged).map((p) => {
    const d = orgData(p.id, now);
    const machines = d?.machines ?? [];
    const failed = machines.filter((m) => m.status === "failed").length;
    const stale = machines.filter((m) => m.status === "stale" || m.status === "never").length;
    return {
      id: p.id,
      name: p.name,
      kind: p.kind,
      parentId: p.parentId,
      machineCount: machines.length,
      protectedBytes: d?.usage.protectedBytes ?? 0,
      storedBytes: d?.usage.storedBytes ?? 0,
      quotaBytes: newOrgQuota.has(p.id) ? newOrgQuota.get(p.id)! : (d?.usage.quotaBytes ?? 0),
      status: closedOrgs.get(p.id) ? "closed" : failed > 0 ? "critical" : stale > 0 ? "attention" : "ok",
      ...closureOf(p.id),
      ...trialOf(p.id),
    };
  });
}

// ---------------------------------------------------------------------------
// Árvore de arquivos sintética para navegação em snapshots

const WIN_TREE: Record<string, string[]> = {
  "C:\\Users": ["financeiro/", "logistica/", "Public/"],
  "C:\\Users\\financeiro": ["Documents/", "Desktop/", "Downloads/"],
  "C:\\Users\\financeiro\\Documents": ["Fechamentos/", "Notas fiscais 2026/", "Contratos/", "orcamento-frota-2027.xlsx", "planilha-pedagios-agosto.xlsx"],
  "C:\\Users\\financeiro\\Documents\\Fechamentos": ["fechamento-julho-2026.xlsx", "fechamento-agosto-2026.xlsx", "conciliacao-bancaria-agosto.pdf", "DRE-1o-semestre-2026.pdf"],
  "C:\\Users\\financeiro\\Documents\\Contratos": ["contrato-locacao-galpao-guarulhos.pdf", "aditivo-seguro-frota-2026.pdf"],
  "D:\\Dados": ["Fiscal/", "Contabilidade/", "Operacional/", "RH/"],
  "D:\\Dados\\Fiscal": ["CT-e 2026/", "MDF-e 2026/", "SPED-EFD-agosto-2026.txt"],
  "C:\\ProgramData\\ERP": ["config/", "backup-banco/", "erp-licenca.lic"],
  "/Users": ["joana.marques/", "Shared/"],
  "/Users/joana.marques": ["Documents/", "Desktop/", "Pictures/"],
  "/Users/joana.marques/Documents": ["Campanhas 2026/", "manual-marca-serra-azul.pdf", "briefing-feira-logistica.key"],
  "/srv": ["tms/", "compartilhado/"],
  "/srv/tms": ["uploads/", "exportacoes/", "tms.env"],
  "/etc": ["nginx/", "postgresql/", "fstab", "hosts"],
  "/var/lib/postgresql": ["16/"],
};

export function listDir(path: string, snapshotId: string, now: number): T.FileEntry[] {
  const rng = mulberry32(hashString(path + snapshotId.slice(0, 6)));
  const names = WIN_TREE[path] ?? ["relatorios/", "arquivo-" + hex(rng, 4) + ".pdf", "planilha-" + hex(rng, 4) + ".xlsx", "notas.txt"];
  return names
    .map((n) => {
      const dir = n.endsWith("/");
      const name = dir ? n.slice(0, -1) : n;
      const ext = name.split(".").pop() ?? "";
      const base = ext === "pdf" ? 900_000 : ext === "xlsx" ? 240_000 : ext === "key" ? 38_000_000 : 12_000;
      return {
        name,
        kind: dir ? ("dir" as const) : ("file" as const),
        size: dir ? 0 : Math.round(base * (0.4 + rng() * 2.5)),
        modifiedAt: iso(now - Math.floor(rng() * 40) * DAY - Math.floor(rng() * 8) * 3600_000),
      };
    })
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name, "pt-BR") : a.kind === "dir" ? -1 : 1));
}

export function newHex(len: number): string {
  return hex(Math.random, len);
}
